<template>
  <section class="relative bg-white md:bg-transparent md:grid md:grid-cols-12 lg:max-w-screen-xl lg:mx-auto xl:max-w-screen-2xl xl:px-12 lg:gap-x-16">
    <div class="relative px-4 pt-8 pb-12 sm:px-12 sm:pt-12 md:bg-white xl:px-20 xl:pt-12 2xl:px-24 2xl:pt-12 md:col-span-10 md:col-start-2 lg:rounded-t-2xl">
      <MoleculeEventDetailPageAudioV2 class="hidden lg:block absolute top-[-4rem] z-10" :audio-work="audioWork" :toggle-header-audio="toggleHeaderAudio" />
      <div class="lg:w-[calc(100%-340px)]">
        <h1
          class="leading-event-header hidden lg:block font-semibold lg:text-4xl xl:text-5xl mb-10"
          :class="[{ 'text-app-secondary-9-green--sherpa-blue': theme !== 'violet', 'text-app-secondary-7-violet--purple-heart': theme === 'violet' }, 't-cdp-title-v2-' + theme]"
        >
          {{ title }}
        </h1>
        <slot name="program"></slot>
        <div class="prose prose--app font-semibold text-sm mb-6" v-html="introduction" />
        <section v-if="body">
          <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out relative">
            <div v-if="!readMoreAboutConcert" class="gradient-overlay" />
            <div class="prose prose--app" :class="{ 'h-auto': readMoreAboutConcert, 'h-14': !readMoreAboutConcert }" v-html="body" />
          </SmoothReflow>
          <button class="underline inline-block" @click="toggleReadMoreAboutConcert">
            {{ readMoreAboutConcert ? $t('event-read-less') : $t('event-read-more') }}
            <AtomIconChevronDown class="ml-2 transform transition duration-200 inline-block w-4 h-4" :class="{ 'rotate-180': readMoreAboutConcert }" />
          </button>
        </section>
      </div>

      <slot name="sideProgram"></slot>

      <ul class="text-base grid gap-4 mb-6 mt-6 lg:w-[calc(100%-320px)] gap-x-0">
        <li v-if="genres.length" class="grid grid-cols-3">
          <h3 class="">{{ $t('event-genre') }}</h3>
          <p class="font-semibold col-span-2 col-start-2">
            <span v-for="(genre, index) in genres" :key="index">
              <span>{{ genre.title }}{{ genres.length > index + 1 ? ', ' : '' }}</span>
            </span>
          </p>
        </li>
        <li v-if="partOfSeries.length" class="grid grid-cols-3">
          <h3>{{ $t('event-part-of-series', partOfSeries.length) }}</h3>
          <AtomLink v-for="(series, seriesIndex) in partOfSeries" :key="seriesIndex" :url="series.url" class="font-semibold underline col-span-2 col-start-2 pb-2"
            >{{ series.title }}{{ partOfSeries.length > seriesIndex + 1 ? ', ' : '' }}
          </AtomLink>
        </li>
        <li v-if="organizer" class="grid grid-cols-3">
          <h3>{{ $t('event-organizer') }}</h3>
          <p class="font-semibold col-span-2 col-start-2">{{ organizer }}</p>
        </li>
      </ul>
      <ul class="text-base grid gap-4 mb-6 mt-6 pt-4 lg:w-[calc(100%-320px)]">
        <li v-if="sponsors.length" class="float-left">
          <h3>{{ $t('event-sponsor-title') }}</h3>
          <p class="font-semibold">
            <span v-for="(sponsor, index) in sponsors" :key="index">
              <span v-if="sponsor.url">
                <!-- eslint-disable-next-line vue/no-html-comments -->
                <a :href="sponsor.url" target="_blank" class="underline">{{ sponsor.title }}</a
                ><span>{{ sponsors.length > index + 1 ? ', ' : '' }}</span>
              </span>
              <span v-else>{{ sponsor.title }}{{ sponsors.length > index + 1 ? ', ' : '' }}</span>
            </span>
          </p>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
const props = defineProps({
  title: { type: String, required: false, default: undefined },
  introduction: { type: String, required: false, default: undefined },
  body: { type: String, required: false, default: undefined },
  organizer: { type: String, required: false, default: undefined },
  genres: { type: Array as PropType<any[]>, required: false, default: () => [] },
  partOfSeries: { type: Array as PropType<any[]>, required: false, default: () => [] },
  theme: { type: String, required: false, default: undefined },
  sponsors: { type: Array as PropType<any[]>, required: false, default: () => [] },
  audioWorks: { type: Array as PropType<any[]>, required: false, default: () => [] },
  headerAudioIsPlaying: { type: Boolean, required: true },
  toggleHeaderAudio: { type: Function, required: true },
})

const audioWork =
  props.audioWorks?.slice().find(({ audio, mainFragment }) => audio?.url && mainFragment) ||
  props.audioWorks
    ?.slice()
    .reverse()
    .find(({ audio }) => audio?.url) ||
  {}
const readMoreAboutConcert = ref(false)
const gtmCdpAction = setupGtmCdpAction()

function toggleReadMoreAboutConcert() {
  readMoreAboutConcert.value = !readMoreAboutConcert.value

  if (readMoreAboutConcert.value) {
    gtmCdpAction('CV07 - About concert read more clicked')
  }
}
</script>

<style lang="scss">
// scoped scss is not suitable for this component, it won't work in this case because it needs to inherit styles from parent elements
.leading-event-header {
  line-height: 1.08; // custom line-height needed for a very important page title (leading-none is too narrow and leading-tight is too much)
}

.gradient-overlay {
  @apply absolute bottom-0 left-0 right-0 h-8;
  background: linear-gradient(to top, white, transparent);
  pointer-events: none; /* Ensure the overlay does not interfere with interactions */
  position: absolute;
  height: 2.5rem;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
